/**
 * Receipt detection and clean-up, done on the phone before anything is sent.
 * Pure functions over pixel arrays (no DOM), so they are unit-tested and
 * shared by the live camera and photos picked from the gallery.
 *
 * A receipt is bright paper on a darker surface: threshold the frame (Otsu),
 * keep the largest bright region, and take its four extreme points as the
 * corners. Good enough to frame the receipt, then a perspective warp
 * straightens it and auto-levels restores faded thermal print.
 */

export interface Point {
  x: number;
  y: number;
}

export interface Detection {
  /** Top-left, top-right, bottom-right, bottom-left (in the frame's pixels). */
  corners: [Point, Point, Point, Point];
  /** Share of the frame the receipt covers (0–1). */
  coverage: number;
  /**
   * The whole receipt is inside the frame. When false it runs off an edge,
   * so part of it (often the total, at the bottom) isn't in the picture.
   */
  complete: boolean;
}

/** Smallest share of the frame that can be a receipt (smaller = too far away / a speck). */
const MIN_COVERAGE = 0.08;
/** A receipt fills most of the quadrilateral through its corners (an L-shape or a hand doesn't). */
const MIN_RECTANGULARITY = 0.8;

function boxBlur(gray: Uint8ClampedArray, w: number, h: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(gray.length);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let sum = 0;
      let n = 0;
      for (let dy = -1; dy <= 1; dy++) {
        const yy = y + dy;
        if (yy < 0 || yy >= h) continue;
        for (let dx = -1; dx <= 1; dx++) {
          const xx = x + dx;
          if (xx < 0 || xx >= w) continue;
          sum += gray[yy * w + xx]!;
          n++;
        }
      }
      out[y * w + x] = sum / n;
    }
  }
  return out;
}

/** Otsu's threshold; null when the frame has no contrast to split. */
function otsu(gray: Uint8ClampedArray): number | null {
  const hist = new Array<number>(256).fill(0);
  for (const v of gray) hist[v]!++;
  let lo = 0;
  let hi = 255;
  while (lo < 255 && hist[lo] === 0) lo++;
  while (hi > 0 && hist[hi] === 0) hi--;
  if (hi - lo < 40) return null;

  const total = gray.length;
  let sumAll = 0;
  for (let i = 0; i < 256; i++) sumAll += i * hist[i]!;
  let sumBack = 0;
  let weightBack = 0;
  let best = 0;
  let threshold = 127;
  for (let t = 0; t < 256; t++) {
    weightBack += hist[t]!;
    if (weightBack === 0) continue;
    const weightFore = total - weightBack;
    if (weightFore === 0) break;
    sumBack += t * hist[t]!;
    const meanBack = sumBack / weightBack;
    const meanFore = (sumAll - sumBack) / weightFore;
    const between = weightBack * weightFore * (meanBack - meanFore) ** 2;
    if (between > best) {
      best = between;
      threshold = t;
    }
  }
  return threshold;
}

const quadArea = (q: Point[]) =>
  Math.abs(q.reduce((sum, p, i) => sum + p.x * q[(i + 1) % q.length]!.y - q[(i + 1) % q.length]!.x * p.y, 0)) / 2;

interface Region {
  labels: Int32Array;
  label: number;
  size: number;
  /** Region pixels on the outermost rows/columns of the frame. */
  onBorder: number;
}

/** Largest 4-connected region brighter than `threshold`. */
function largestRegion(smooth: Uint8ClampedArray, w: number, h: number, threshold: number): Region | null {
  const labels = new Int32Array(w * h).fill(-1);
  const stack: number[] = [];
  let best: Region | null = null;
  let label = 0;
  for (let start = 0; start < w * h; start++) {
    if (labels[start] !== -1 || smooth[start]! <= threshold) continue;
    let size = 0;
    let onBorder = 0;
    stack.push(start);
    labels[start] = label;
    while (stack.length) {
      const i = stack.pop()!;
      size++;
      const x = i % w;
      const y = (i - x) / w;
      if (x === 0 || y === 0 || x === w - 1 || y === h - 1) onBorder++;
      const neighbours = [x > 0 ? i - 1 : -1, x < w - 1 ? i + 1 : -1, i - w, i + w];
      for (const n of neighbours) {
        if (n < 0 || n >= w * h || labels[n] !== -1 || smooth[n]! <= threshold) continue;
        labels[n] = label;
        stack.push(n);
      }
    }
    if (!best || size > best.size) best = { labels, label, size, onBorder };
    label++;
  }
  return best;
}

/** The region's four corners: its extreme points along the two diagonals. */
function cornersOf(region: Region, w: number, h: number): Detection['corners'] {
  let tl = { x: 0, y: 0, s: Infinity };
  let br = { x: 0, y: 0, s: -Infinity };
  let tr = { x: 0, y: 0, s: -Infinity };
  let bl = { x: 0, y: 0, s: Infinity };
  for (let i = 0; i < w * h; i++) {
    if (region.labels[i] !== region.label) continue;
    const x = i % w;
    const y = (i - x) / w;
    if (x + y < tl.s) tl = { x, y, s: x + y };
    if (x + y > br.s) br = { x, y, s: x + y };
    if (x - y > tr.s) tr = { x, y, s: x - y };
    if (x - y < bl.s) bl = { x, y, s: x - y };
  }
  return [
    { x: tl.x, y: tl.y },
    { x: tr.x, y: tr.y },
    { x: br.x, y: br.y },
    { x: bl.x, y: bl.y },
  ];
}

function percentile(gray: Uint8ClampedArray, p: number): number {
  const hist = new Array<number>(256).fill(0);
  for (const v of gray) hist[v]!++;
  let seen = 0;
  for (let v = 0; v < 256; v++) {
    seen += hist[v]!;
    if (seen >= gray.length * p) return v;
  }
  return 255;
}

/**
 * Finds the receipt in a grayscale frame (w×h), or null if there isn't a
 * clear one. Paper is usually the brightest thing in view, but a light desk
 * can pass Otsu's threshold too, so stricter thresholds (towards the
 * brightest pixels) are tried until a receipt-shaped region lies fully
 * inside the frame. If none does, the best partial one is returned with
 * `complete: false`.
 */
export function detectReceipt(gray: Uint8ClampedArray, w: number, h: number): Detection | null {
  const smooth = boxBlur(gray, w, h);
  const base = otsu(smooth);
  if (base === null) return null;
  const top = Math.max(base + 1, percentile(smooth, 0.98));

  let partial: Detection | null = null;
  for (const step of [0, 0.25, 0.5, 0.7]) {
    const threshold = Math.round(base + (top - base) * step);
    const region = largestRegion(smooth, w, h, threshold);
    if (!region) continue;
    const share = region.size / (w * h);
    if (share < MIN_COVERAGE || share > 0.97) continue;
    const corners = cornersOf(region, w, h);
    const area = quadArea(corners);
    if (area / (w * h) < MIN_COVERAGE || region.size / area < MIN_RECTANGULARITY) continue;
    const complete = region.onBorder === 0;
    const detection = { corners, coverage: area / (w * h), complete };
    if (complete) return detection;
    partial ??= detection;
  }
  return partial;
}

/** 3×3 homography (row-major, h[8] = 1) mapping each `from` point onto `to`. */
export function solveHomography(from: Point[], to: Point[]): number[] {
  // Eight linear equations in h0…h7 (Gaussian elimination with pivoting).
  const a: number[][] = [];
  for (let i = 0; i < 4; i++) {
    const { x, y } = from[i]!;
    const { x: u, y: v } = to[i]!;
    a.push([x, y, 1, 0, 0, 0, -u * x, -u * y, u]);
    a.push([0, 0, 0, x, y, 1, -v * x, -v * y, v]);
  }
  for (let col = 0; col < 8; col++) {
    let pivot = col;
    for (let r = col + 1; r < 8; r++) if (Math.abs(a[r]![col]!) > Math.abs(a[pivot]![col]!)) pivot = r;
    [a[col], a[pivot]] = [a[pivot]!, a[col]!];
    for (let r = 0; r < 8; r++) {
      if (r === col) continue;
      const f = a[r]![col]! / a[col]![col]!;
      for (let c = col; c < 9; c++) a[r]![c]! -= f * a[col]![c]!;
    }
  }
  return [...a.map((row, i) => row[8]! / row[i]!), 1];
}

export function mapPoint(h: number[], p: Point): Point {
  const d = h[6]! * p.x + h[7]! * p.y + h[8]!;
  return { x: (h[0]! * p.x + h[1]! * p.y + h[2]!) / d, y: (h[3]! * p.x + h[4]! * p.y + h[5]!) / d };
}

/**
 * Stretches contrast in place (RGBA) so the 2nd–98th percentile of
 * brightness spans the full range: faded thermal print becomes legible.
 */
export function autoLevels(rgba: Uint8ClampedArray): void {
  const hist = new Array<number>(256).fill(0);
  const pixels = rgba.length / 4;
  for (let i = 0; i < rgba.length; i += 4) {
    hist[Math.round(0.299 * rgba[i]! + 0.587 * rgba[i + 1]! + 0.114 * rgba[i + 2]!)]!++;
  }
  const percentile = (p: number) => {
    let seen = 0;
    for (let v = 0; v < 256; v++) {
      seen += hist[v]!;
      if (seen >= pixels * p) return v;
    }
    return 255;
  };
  const low = percentile(0.02);
  const high = percentile(0.98);
  if (high - low < 20) return;
  const scale = 255 / (high - low);
  for (let i = 0; i < rgba.length; i += 4) {
    rgba[i] = (rgba[i]! - low) * scale;
    rgba[i + 1] = (rgba[i + 1]! - low) * scale;
    rgba[i + 2] = (rgba[i + 2]! - low) * scale;
  }
}

/** Variance of the Laplacian: higher = sharper (used to wait for a steady, focused frame). */
export function sharpness(gray: Uint8ClampedArray, w: number, h: number): number {
  let sum = 0;
  let sumSq = 0;
  let n = 0;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      const lap = gray[i - 1]! + gray[i + 1]! + gray[i - w]! + gray[i + w]! - 4 * gray[i]!;
      sum += lap;
      sumSq += lap * lap;
      n++;
    }
  }
  const mean = sum / n;
  return sumSq / n - mean * mean;
}

/**
 * "How much like receipt paper" each pixel is: brightness minus colourfulness.
 * Paper is neutral (white/grey, even in dim or warm light), while desks are
 * usually coloured (brown wood, tiles), so this separates them where plain
 * brightness can't (e.g. white paper on light wood in a dim room).
 */
export function toPaperness(rgba: Uint8ClampedArray): Uint8ClampedArray {
  const out = new Uint8ClampedArray(rgba.length / 4);
  for (let i = 0, j = 0; i < rgba.length; i += 4, j++) {
    const r = rgba[i]!;
    const g = rgba[i + 1]!;
    const b = rgba[i + 2]!;
    const chroma = Math.max(r, g, b) - Math.min(r, g, b);
    out[j] = 0.299 * r + 0.587 * g + 0.114 * b - 1.5 * chroma;
  }
  return out;
}

/** Grayscale copy of RGBA pixels. */
export function toGray(rgba: Uint8ClampedArray): Uint8ClampedArray {
  const gray = new Uint8ClampedArray(rgba.length / 4);
  for (let i = 0, j = 0; i < rgba.length; i += 4, j++) {
    gray[j] = 0.299 * rgba[i]! + 0.587 * rgba[i + 1]! + 0.114 * rgba[i + 2]!;
  }
  return gray;
}
